import React from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash-es';
import { isEmail } from 'validator';
import Icon from 'utils/components/icon';
import { autobind } from 'core-decorators';
import SearchUserDropdown from '../dropdown/search-user';
import UsersAutocomplete from '../autocomplete/users';

import { Error } from '../../../../scripts/components/topics/components/error/error';
import { findUserByEmail } from '../../moderation-v2/comments-and-posts/api/find-user';
import { CSVLink } from 'react-csv';
import { findUserByName } from '../../../../users/usersActions';
import { connect } from 'react-redux';
import { parseUsers } from '../../../../users/usersUtils';
import { clearSuggestions } from '../../../../users/usersSlice';

class SearchUserInput extends React.Component {
  static displayName = 'SearchUserInput';

  static propTypes = {
    spotId: PropTypes.string.isRequired,
    onSearchTypeChange: PropTypes.func,
    placeholder: PropTypes.string,
    searchUserBy: PropTypes.oneOf(['email', 'username']),
    teammates: PropTypes.array,
    suggestions: PropTypes.array,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    onResetDone: PropTypes.func,
    status: PropTypes.string,
    isViewOnlyMode: PropTypes.bool,
    userControls: PropTypes.object,
    hasExport: PropTypes.bool,
    findUser: PropTypes.func
  };

  constructor(props, context) {
    super(props, context);
    this.state = { tooltipVisible: false, value: props.value || '', suggestions: [] };
    this.userListRef = React.createRef();
  }

  componentDidMount() {
    this.props.clearSuggestions();
  }

  componentDidUpdate() {
    window.addEventListener('click', this.onWindowClicked.bind(this));
  }

  componentWillUnMount() {
    window.removeEventListener('click', this.onWindowClicked.bind(this));
    this.props.clearSuggestions();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    let { resetValue, onResetDone } = nextProps;
    if (!this.props.resetValue && resetValue && onResetDone) {
      this.setState({ value: '' }, onResetDone);
    }
  }

  onWindowClicked(event) {
    var element = this.userListRef?.current;
    var isContain = false;
    if (element) {
      isContain = element.contains(event.target);

      if (this.state.tooltipVisible !== isContain) {
        this.setState({ tooltipVisible: isContain });
      }
    }
  }

  @autobind
  onSearchTypeChange(type) {
    this.props.onSearchTypeChange(type);
  }

  @autobind
  onItemSelected() {
    this.setState({ value: '', tooltipVisible: false });
  }

  onChange = event => {
    const value = event.target.value;
    this.onSearchChange(value);
    this.setState({ value });
  };

  onSearchChange = debounce(value => {
    let { spotId, searchUserBy } = this.props;
    if (searchUserBy === 'username') {
      if (value.length > 0) {
        this.props.findUser(value);
        this.setState({ value, tooltipVisible: true });
      }
    }

    if (value.length === 0) {
      this.setState({ value, tooltipVisible: false });
    }

    this.setState({ value });
  }, 200);

  async searchUser() {
    let { spotId, searchUserBy } = this.props;
    let { value } = this.state;
    if (value.length > 1 && searchUserBy === 'email' && isEmail(value)) {
      try {
        let { suggestions } = await findUserByEmail(value);
        suggestions = parseUsers(suggestions);
        this.setState({ suggestions, tooltipVisible: true });
      } catch (e) {
        console.error('Failed to find email', e, value);
      }
    }
  }

  @autobind
  onKeyPress(event) {
    var ENTER = 13;
    if (event.charCode === ENTER) {
      this.searchUser();
    }
  }

  @autobind
  resetSearch() {
    this.setState({ value: '', suggestions: [], tooltipVisible: false });
  }

  render() {
    let { value, tooltipVisible } = this.state;
    let {
      placeholder,
      searchUserBy,
      suggestions,
      spotId,
      teammates,
      status,
      title,
      description,
      type,
      eventId,
      showError,
      isViewOnlyMode,
      userControls,
      hasExport
    } = this.props;
    const csvData = teammates?.map(teammate => ({
      username: teammate.username,
      displayName: teammate.displayName,
      email: teammate.email,
      roles: teammate.roles.filter(role => role !== 'registered')
    }));
    const headers = [
      { label: 'Username', key: 'username' },
      { label: 'Display Name', key: 'displayName' },
      { label: 'Email', key: 'email' },
      { label: 'Roles', key: 'roles' }
    ];
    var suggestionsToolTip = null,
      resetButton = null;
    if (tooltipVisible) {
      if (searchUserBy === 'email') {
        suggestions = this.state.suggestions;
        if (suggestions.length > 0) {
          resetButton = (
            <span className="close" onClick={this.resetSearch}>
              <Icon name="close" className="close-icon" />
            </span>
          );
        }
        if (status === 'loading') {
          suggestionsToolTip = (
            <div className="autocomplete-no-result-item">
              <span className="spinner" />
            </div>
          );
        } else if (suggestions.length === 0 && status === 'success') {
          suggestionsToolTip = (
            <div ref={this.userListRef} className="autocomplete-no-result-item">
              No results.
            </div>
          );
        }
      }
      if (suggestions.length > 0) {
        suggestionsToolTip = (
          <div ref={this.userListRef}>
            <UsersAutocomplete
              resetSearch={this.resetSearch}
              eventId={eventId}
              onItemSelected={this.onItemSelected}
              spotId={spotId}
              type={type}
              className="assign-user-autocomplete"
              users={suggestions}
              teammates={teammates}
              isViewOnlyMode={isViewOnlyMode}
              userControls={userControls}
            />
          </div>
        );
      }
    }

    let className = 'assign-user field';
    if (this.props.className) {
      className += ` ${this.props.className}`;
    }

    const error =
      showError === true ? (
        <Error
          wrapperStyle={{ position: 'absolute', top: 39, right: 1 }}
          toolTipStyle={{ top: -76, width: 227 }}
          message="Please assign at least one page Admin."
        />
      ) : null;

    const inpStyle = showError ? { border: '1px solid rgba(219, 55, 55, 0.5)' } : {};

    return (
      <div className={className}>
        <div className="field-wrapper">
          <div>
            {title ? <div className="field-title">{title}</div> : null}
            <div className="field-description"> {description} </div>
          </div>
          {hasExport && (
            <div className="export-btn">
              <CSVLink
                data={csvData}
                headers={headers}
                filename="team_members.csv"
                encoding="UTF-8"
                style={{ textDecoration: 'none' }}
              >
                <div className="inner-export-button">
                  <Icon name="export_icon" className="export-csv" />
                  <span className="text">Export as CSV</span>
                </div>
              </CSVLink>
            </div>
          )}
        </div>

        <div className="assign-user-input">
          <div className="serach-user-dropdown-wrapper">
            <SearchUserDropdown
              className="assign-user-dropdown"
              searchUserBy={searchUserBy}
              onSearchTypeChange={this.onSearchTypeChange}
              isTextOption={false}
            />
          </div>
          <div className="text-field-regular">
            <input
              type="text"
              value={value}
              onKeyPress={this.onKeyPress}
              placeholder={placeholder}
              onChange={this.onChange}
              style={inpStyle}
            />
            {error}
            {resetButton}
            {suggestionsToolTip}
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  findUser: username => dispatch(findUserByName(username)),
  clearSuggestions: () => dispatch(clearSuggestions())
});

const mapStateToProps = state => {
  return {
    suggestions: state.users.suggestions,
    status: state.users.status
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchUserInput);
